import { useCallback, useState } from 'react';
import { notification } from 'tea-component';
import useLanguage from '@op/hooks/useLanguage';
import {
  setReportPermission,
  type IAuthorizedUser,
  type IReportPermissionTarget,
  type TPermissionLevel,
} from '@op/services/report';
import type { TReportPermissionNotificationMode } from '../ReportPermissionProvider';
import {
  showReportPermissionAdjustedNotification,
  showReportPermissionUpdateSuccessNotification,
} from '../utils/permissionNotification';

interface ISaveReportPermissionArgs {
  target: IReportPermissionTarget;
  permissionLevel: TPermissionLevel;
  selectedUsers: IAuthorizedUser[];
}

export function useSaveReportPermission({
  onSaved,
  onClose,
  notificationMode = 'list',
  onPermissionLinkClick,
}: {
  onSaved?: () => void;
  onClose: () => void;
  notificationMode?: TReportPermissionNotificationMode;
  onPermissionLinkClick?: (target: IReportPermissionTarget) => void;
}) {
  const { formatMessage } = useLanguage();
  const [saving, setSaving] = useState(false);

  const save = useCallback(
    async ({
      target,
      permissionLevel,
      selectedUsers,
    }: ISaveReportPermissionArgs) => {
      if (permissionLevel === 'partial' && selectedUsers.length === 0) {
        return;
      }

      setSaving(true);
      try {
        await setReportPermission({
          data: {
            ...target,
            permission_level: permissionLevel,
            authorized_users:
              permissionLevel === 'partial'
                ? selectedUsers.map(user => ({ user_id: user.user_id }))
                : [],
          },
        });

        onClose();
        onSaved?.();

        if (notificationMode === 'list') {
          showReportPermissionUpdateSuccessNotification({ formatMessage });
        } else {
          showReportPermissionAdjustedNotification({
            formatMessage,
            permissionLevel,
            onPermissionClick: () => onPermissionLinkClick?.(target),
          });
        }
      } catch (error: any) {
        const description =
          error?.code === 30020
            ? formatMessage({ id: 'No report permission' })
            : error?.message || formatMessage({ id: 'Request failed' });
        notification.error({ description });
      } finally {
        setSaving(false);
      }
    },
    [formatMessage, notificationMode, onClose, onPermissionLinkClick, onSaved],
  );

  return {
    saving,
    save,
  };
}
